// Browser-side helper for calling the site's own API routes.

export class RequestError extends Error {
    status: number
    code?: string

    constructor(message: string, status: number, code?: string) {
        super(message)
        this.status = status
        this.code = code
    }
}

type RequestOptions = {
    method?: 'GET' | 'POST'
    body?: unknown
    signal?: AbortSignal
}

export async function api<T>(path: string, { method, body, signal }: RequestOptions = {}): Promise<T> {
    const verb = method ?? (body === undefined ? 'GET' : 'POST')
    let response: Response

    try {
        response = await fetch(path, {
            method: verb,
            headers: verb === 'GET' ? undefined : { 'Content-Type': 'application/json' },
            body: verb === 'GET' ? undefined : JSON.stringify(body ?? {}),
            credentials: 'same-origin',
            cache: 'no-store',
            signal
        })
    }
    catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') throw error

        throw new RequestError('Could not reach DonutFire. Check your connection and try again.', 0)
    }

    const data = (await response.json().catch(() => null)) as (T & { error?: string, code?: string }) | null

    if (!response.ok) {
        throw new RequestError(data?.error ?? 'Something went wrong. Please try again.', response.status, data?.code)
    }

    return data as T
}

export function errorMessage(error: unknown): string {
    if (error instanceof Error && error.message) return error.message

    return 'Something went wrong. Please try again.'
}

export function isAbort(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'AbortError'
}
